import * as React from 'react'
import { ChangeEvent } from 'react'
import { Button, Callout, Classes, Dialog, Intent, Label, TextArea } from '@blueprintjs/core'
import { Link } from 'react-router'
import Client, { AddAdditionalDetailsChannelNames } from '../../../../../../client'
import { showToast } from '../../../../../../Toast'
import { UprightStyle } from '../../../../../../types'
import { Channel } from '../../types'

interface Props {
  listingId: number
  onClose: (changePerformed?: boolean) => void
  channel: Channel
  bidCount?: number
}

const style: UprightStyle = {
  callout: { marginBottom: 8 },
  paragraphExplanation: { marginBottom: 20 },
  label: { margin: 0 },
  textArea: { height: 70 }
}

const AddAdditionalDetailsDialog: React.FC<Props> = ({ listingId, bidCount, channel, onClose }) => {
  const [additionalDetails, setAdditionalDetails] = React.useState('')
  const [submitAdditionalInProgress, setSubmitAdditionalInProgress] = React.useState(false)
  const [error, setError] = React.useState(null)

  async function submit() {
    setSubmitAdditionalInProgress(true)

    try {
      setError(false)

      await Client.v5AddAdditionalDetails(
        listingId,
        additionalDetails,
        channel.name as AddAdditionalDetailsChannelNames
      )

      showToast('Success!')
      onClose(true)
    } catch (_e) {
      setError('Something went wrong. Please try again.') // toasts appear behind the dialog overlay, so use a callout inside the dialog
    }
    setSubmitAdditionalInProgress(false)
  }

  return (
    <Dialog icon="bring-data" onClose={() => onClose(false)} isOpen title="Add additional information">
      <div className={Classes.DIALOG_BODY}>
        {bidCount != null && bidCount === 0 && (
          <Callout intent="warning" style={style.callout}>
            This listing doesn&apos;t seem to have any bids. Instead of choosing &quot;add additional information&quot;,
            you can <Link to={`/listings/${channel.name}/${listingId}/edit`}>edit the listing</Link> instead.
          </Callout>
        )}

        {error && (
          <Callout intent="danger" style={style.callout}>
            {error}
          </Callout>
        )}
        <p className="bp3-text-muted" style={style.paragraphExplanation}>
          Enter additional info for your {channel.humanReadableName} listing. This allows updating the description after
          the item has bids.
        </p>
        <Label style={style.label}>
          Additional information
          <TextArea
            id={`additional-details-dialog-${listingId}`}
            placeholder="It does not include a charger"
            className="bp3-fill"
            style={style.textArea}
            value={additionalDetails}
            onChange={(changeEvent: ChangeEvent<HTMLTextAreaElement>) =>
              setAdditionalDetails(changeEvent.currentTarget.value)
            }
          />
        </Label>
      </div>

      <div className={Classes.DIALOG_FOOTER}>
        <div className={Classes.DIALOG_FOOTER_ACTIONS}>
          <Button onClick={() => onClose(false)} disabled={submitAdditionalInProgress}>
            Cancel
          </Button>
          <Button
            intent={Intent.PRIMARY}
            // submit is stable for this dialog lifetime; eslint flags function refs as jsx-no-bind
            // eslint-disable-next-line react/jsx-no-bind
            onClick={submit}
            loading={submitAdditionalInProgress}
            disabled={additionalDetails.trim().length === 0}
          >
            Add info
          </Button>
        </div>
      </div>
    </Dialog>
  )
}

export default AddAdditionalDetailsDialog
